import { useState, useEffect, useRef, useCallback } from 'react';
import ImageModal from '@/components/feature/ImageModal';

interface CameraControlProps {
  deviceId: string;
}

interface ServoLog {
  no: number;
  camera: string;
  angle: number;
  is_photo_received: string;
  photo_url: string;
  regdate: string;
  photo_received_date: string;
}

const ANGLE_MIN = 60;
const ANGLE_MAX = 120;
const ANGLE_DEFAULT = 90;
const TICK_MARKS = [60, 75, 90, 105, 120];
const PRESETS = [
  { label: '좌60°', value: 60 },
  { label: '좌75°', value: 75 },
  { label: '정면90°', value: 90 },
  { label: '우105°', value: 105 },
  { label: '우120°', value: 120 },
];
const POLL_INTERVAL = 2000;
const MAX_POLL_ATTEMPTS = 15;
const ONLINE_THRESHOLD_MINUTES = 15;

function isRecentlyOnline(isOnline: string, updateDate: string | undefined): boolean {
  if (isOnline !== 'Y') return false;
  if (!updateDate) return false;
  try {
    const lastUpdate = new Date(updateDate.replace(' ', 'T'));
    if (isNaN(lastUpdate.getTime())) return false;
    const diffMs = Date.now() - lastUpdate.getTime();
    return diffMs >= 0 && diffMs < ONLINE_THRESHOLD_MINUTES * 60 * 1000;
  } catch {
    return false;
  }
}

export default function CameraControl({ deviceId }: CameraControlProps) {
  const [rgb1Angle, setRgb1Angle] = useState(ANGLE_DEFAULT);
  const [rgb2Angle, setRgb2Angle] = useState(ANGLE_DEFAULT);
  const [localRgb1, setLocalRgb1] = useState(ANGLE_DEFAULT);
  const [localRgb2, setLocalRgb2] = useState(ANGLE_DEFAULT);
  const [rgb1Photo, setRgb1Photo] = useState('');
  const [rgb2Photo, setRgb2Photo] = useState('');
  const [rgb1Status, setRgb1Status] = useState('');
  const [rgb2Status, setRgb2Status] = useState('');
  const [isOnline, setIsOnline] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [modalImage, setModalImage] = useState<{ url: string; label: string } | null>(null);

  const lastLogIdRef = useRef(0);
  const pollingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollingAttemptsRef = useRef(0);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const sliderDebounceRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const rgb1WaitingRef = useRef(false);
  const rgb2WaitingRef = useRef(false);

  const showToast = useCallback((msg: string) => {
    setToastMsg(msg);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToastMsg(''), 2500);
  }, []);

  const stopPolling = useCallback(() => {
    if (pollingTimerRef.current) {
      clearInterval(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
    pollingAttemptsRef.current = 0;
  }, []);

  const poll = useCallback(async () => {
    try {
      pollingAttemptsRef.current += 1;
      const res = await fetch(
        `https://tof.eemo.co.kr/api/api_servo_poll.php?device_id=${deviceId}&after_id=${lastLogIdRef.current}`,
      );
      const data = await res.json();

      if (data.result !== 'success') return;

      // Sync angles
      if (typeof data.rgb1_angle === 'number') {
        setRgb1Angle(data.rgb1_angle);
        setLocalRgb1(data.rgb1_angle);
      }
      if (typeof data.rgb2_angle === 'number') {
        setRgb2Angle(data.rgb2_angle);
        setLocalRgb2(data.rgb2_angle);
      }

      // Sync online status (is_online='Y' + update_date within 15 min)
      setIsOnline(isRecentlyOnline(data.is_online, data.update_date));

      // Check for new still cuts
      const logs: ServoLog[] = data.logs || [];
      const newPhotos = logs.filter(
        (log: ServoLog) => log.is_photo_received === 'Y' && log.photo_url,
      );

      if (newPhotos.length > 0) {
        for (const log of newPhotos) {
          const photoUrl = log.photo_url + '?t=' + Date.now();
          if (log.camera === 'rgb1' && rgb1WaitingRef.current) {
            setRgb1Photo(photoUrl);
            setRgb1Angle(log.angle);
            setLocalRgb1(log.angle);
            setRgb1Status('스틸컷 수신 완료 ✓');
            rgb1WaitingRef.current = false;
          } else if (log.camera === 'rgb2' && rgb2WaitingRef.current) {
            setRgb2Photo(photoUrl);
            setRgb2Angle(log.angle);
            setLocalRgb2(log.angle);
            setRgb2Status('스틸컷 수신 완료 ✓');
            rgb2WaitingRef.current = false;
          }
          if (log.no > lastLogIdRef.current) {
            lastLogIdRef.current = log.no;
          }
        }

        if (!rgb1WaitingRef.current && !rgb2WaitingRef.current) {
          stopPolling();
          return;
        }
      }

      // Max attempts check
      if (pollingAttemptsRef.current >= MAX_POLL_ATTEMPTS) {
        if (rgb1WaitingRef.current) {
          setRgb1Status('스틸컷 수신 시간 초과 - 장비가 오프라인일 수 있습니다');
          rgb1WaitingRef.current = false;
        }
        if (rgb2WaitingRef.current) {
          setRgb2Status('스틸컷 수신 시간 초과 - 장비가 오프라인일 수 있습니다');
          rgb2WaitingRef.current = false;
        }
        stopPolling();
      }
    } catch {
      // ignore poll errors
    }
  }, [deviceId, stopPolling]);

  const startPolling = useCallback(() => {
    stopPolling();
    pollingAttemptsRef.current = 0;
    pollingTimerRef.current = setInterval(() => {
      poll();
    }, POLL_INTERVAL);
  }, [stopPolling, poll]);

  const executeCommand = useCallback(
    async (camera: 'rgb1' | 'rgb2', angle: number) => {
      try {
        const res = await fetch(
          `https://tof.eemo.co.kr/api/api_servo_control.php?device_id=${deviceId}&camera=${camera}&angle=${angle}`,
        );
        const data = await res.json();
        if (data.result === 'success') {
          if (data.log_id) {
            lastLogIdRef.current = data.log_id;
          }
          if (camera === 'rgb1') {
            setRgb1Status('명령 전송됨... 스틸컷 대기중');
            rgb1WaitingRef.current = true;
          } else {
            setRgb2Status('명령 전송됨... 스틸컷 대기중');
            rgb2WaitingRef.current = true;
          }
          startPolling();
        }
      } catch {
        showToast('서보 명령 전송에 실패했습니다.');
      }
    },
    [deviceId, startPolling, showToast],
  );

  const handleSliderChange = useCallback(
    (camera: 'rgb1' | 'rgb2', value: number) => {
      const clamped = Math.round(Math.max(ANGLE_MIN, Math.min(ANGLE_MAX, value)));
      if (camera === 'rgb1') setLocalRgb1(clamped);
      else setLocalRgb2(clamped);

      if (sliderDebounceRef.current[camera]) {
        clearTimeout(sliderDebounceRef.current[camera]);
      }
      sliderDebounceRef.current[camera] = setTimeout(() => {
        executeCommand(camera, clamped);
      }, 500);
    },
    [executeCommand],
  );

  const handleImmediate = useCallback(
    (camera: 'rgb1' | 'rgb2', angle: number) => {
      const clamped = Math.round(Math.max(ANGLE_MIN, Math.min(ANGLE_MAX, angle)));
      if (camera === 'rgb1') setLocalRgb1(clamped);
      else setLocalRgb2(clamped);
      executeCommand(camera, clamped);
    },
    [executeCommand],
  );

  // Initial load
  useEffect(() => {
    if (!deviceId) return;
    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(
          `https://tof.eemo.co.kr/api/api_servo_poll.php?device_id=${deviceId}&after_id=0`,
        );
        const data = await res.json();
        if (cancelled || data.result !== 'success') return;

        if (typeof data.rgb1_angle === 'number') {
          setRgb1Angle(data.rgb1_angle);
          setLocalRgb1(data.rgb1_angle);
        }
        if (typeof data.rgb2_angle === 'number') {
          setRgb2Angle(data.rgb2_angle);
          setLocalRgb2(data.rgb2_angle);
        }
        setIsOnline(isRecentlyOnline(data.is_online, data.update_date));

        // Load last still cuts
        const logs: ServoLog[] = data.logs || [];
        for (const log of logs) {
          if (log.is_photo_received === 'Y' && log.photo_url) {
            const photoUrl = log.photo_url + '?t=' + Date.now();
            if (log.camera === 'rgb1') {
              setRgb1Photo(photoUrl);
            } else if (log.camera === 'rgb2') {
              setRgb2Photo(photoUrl);
            }
            if (log.no > lastLogIdRef.current) {
              lastLogIdRef.current = log.no;
            }
          }
        }
      } catch {
        // ignore init errors
      }
    })();

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
      Object.values(sliderDebounceRef.current).forEach(clearTimeout);
      sliderDebounceRef.current = {};
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, [stopPolling]);

  return (
    <>
      <div className="relative bg-white rounded-2xl border border-gray-100 overflow-hidden">
        {/* Header */}
        <div
          className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-2"
          style={{ backgroundColor: '#f0f9f4' }}
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-gamepad-line text-green-600"></i>
          </div>
          <span className="font-bold text-gray-800 text-sm">카메라 각도 제어</span>
          <div className="ml-auto flex items-center gap-2">
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                isOnline ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
              }`}
            >
              <span
                className={`w-2 h-2 rounded-full inline-block ${isOnline ? 'bg-green-500' : 'bg-red-500'}`}
              ></span>
              {isOnline ? '장비 온라인' : '장비 오프라인'}
            </span>
          </div>
        </div>

        {/* Offline Overlay */}
        {!isOnline && (
          <div className="absolute inset-0 z-10 flex items-center justify-center" style={{ top: 49 }}>
            <div className="absolute inset-0 bg-white/60 rounded-b-2xl"></div>
            <div className="relative z-20 bg-white/90 backdrop-blur-sm rounded-xl px-6 py-4 border border-gray-200 text-center max-w-xs mx-4">
              <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 rounded-full bg-red-50">
                <i className="ri-wifi-off-line text-red-400 text-xl"></i>
              </div>
              <p className="text-gray-700 text-sm font-semibold mb-1">장비가 오프라인 상태입니다</p>
              <p className="text-gray-500 text-xs">타이머 활성 시간에만 제어 가능합니다.</p>
            </div>
          </div>
        )}

        {/* Toast */}
        {toastMsg && (
          <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-gray-900 text-white text-sm px-5 py-2.5 rounded-xl shadow-lg whitespace-nowrap animate-pulse">
            {toastMsg}
          </div>
        )}

        {/* Camera Panels */}
        <div className={`p-4 md:p-5 ${!isOnline ? 'opacity-30 pointer-events-none' : ''}`}>
          <div className="flex flex-col md:flex-row gap-5">
            <CameraPanel
              label="LEFT RGB (RGB1)"
              icon="ri-camera-line"
              color="#27ae60"
              angle={localRgb1}
              photoUrl={rgb1Photo}
              status={rgb1Status}
              disabled={!isOnline}
              onSliderChange={(v) => handleSliderChange('rgb1', v)}
              onAdjust={(delta) => handleImmediate('rgb1', localRgb1 + delta)}
              onPreset={(v) => handleImmediate('rgb1', v)}
              onPhotoClick={() => {
                if (rgb1Photo) {
                  setModalImage({ url: rgb1Photo.split('?')[0], label: 'LEFT RGB (RGB1) 스틸컷' });
                }
              }}
            />
            <CameraPanel
              label="RIGHT RGB (RGB2)"
              icon="ri-camera-line"
              color="#e67e22"
              angle={localRgb2}
              photoUrl={rgb2Photo}
              status={rgb2Status}
              disabled={!isOnline}
              onSliderChange={(v) => handleSliderChange('rgb2', v)}
              onAdjust={(delta) => handleImmediate('rgb2', localRgb2 + delta)}
              onPreset={(v) => handleImmediate('rgb2', v)}
              onPhotoClick={() => {
                if (rgb2Photo) {
                  setModalImage({ url: rgb2Photo.split('?')[0], label: 'RIGHT RGB (RGB2) 스틸컷' });
                }
              }}
            />
          </div>
        </div>
      </div>

      {modalImage && (
        <ImageModal
          isOpen={true}
          imageUrl={modalImage.url}
          label={modalImage.label}
          onClose={() => setModalImage(null)}
        />
      )}
    </>
  );
}

interface CameraPanelProps {
  label: string;
  icon: string;
  color: string;
  angle: number;
  photoUrl: string;
  status: string;
  disabled: boolean;
  onSliderChange: (value: number) => void;
  onAdjust: (delta: number) => void;
  onPreset: (value: number) => void;
  onPhotoClick: () => void;
}

function CameraPanel({
  label,
  icon,
  color,
  angle,
  photoUrl,
  status,
  disabled,
  onSliderChange,
  onAdjust,
  onPreset,
  onPhotoClick,
}: CameraPanelProps) {
  const sliderPercent = ((angle - ANGLE_MIN) / (ANGLE_MAX - ANGLE_MIN)) * 100;

  return (
    <div className="flex-1 min-w-0 bg-gray-50 rounded-xl p-4">
      {/* Still Cut Image */}
      <div
        className="relative w-full aspect-video bg-gray-200 rounded-lg mb-3 overflow-hidden cursor-pointer group"
        onClick={photoUrl ? onPhotoClick : undefined}
      >
        {photoUrl ? (
          <img
            src={photoUrl}
            alt={`${label} still cut`}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400">
            <div className="w-8 h-8 flex items-center justify-center mb-1">
              <i className="ri-image-line text-2xl"></i>
            </div>
            <span className="text-xs">대기중...</span>
          </div>
        )}
        {photoUrl && (
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/80 opacity-0 group-hover:opacity-100 transition-opacity">
              <i className="ri-zoom-in-line text-gray-700 text-lg"></i>
            </div>
          </div>
        )}
      </div>

      {/* Label + Current Angle */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div
            className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0"
            style={{ backgroundColor: color + '18' }}
          >
            <i className={icon} style={{ color }}></i>
          </div>
          <span className="font-semibold text-gray-700 text-sm whitespace-nowrap">{label}</span>
        </div>
        <span className="text-lg font-bold whitespace-nowrap" style={{ color }}>
          {angle}°
        </span>
      </div>

      {/* Status Line */}
      {status && (
        <div
          className={`text-xs mb-3 px-2 py-1 rounded-md inline-block whitespace-nowrap ${
            status.includes('완료')
              ? 'bg-green-50 text-green-700'
              : status.includes('초과') || status.includes('오프라인')
                ? 'bg-red-50 text-red-600'
                : 'bg-blue-50 text-blue-600'
          }`}
        >
          {status}
        </div>
      )}

      {/* Slider */}
      <div className="mb-3 px-1">
        <div className="relative">
          <input
            type="range"
            min={ANGLE_MIN}
            max={ANGLE_MAX}
            step={1}
            value={angle}
            disabled={disabled}
            onChange={(e) => onSliderChange(Number(e.target.value))}
            className="w-full h-2 rounded-full appearance-none cursor-pointer"
            style={{
              background: `linear-gradient(to right, ${color}30 0%, ${color} ${sliderPercent}%, #e5e7eb ${sliderPercent}%, #e5e7eb 100%)`,
              accentColor: color,
            }}
          />
        </div>
        <div className="flex justify-between mt-1 px-0.5">
          {TICK_MARKS.map((tick) => (
            <span
              key={tick}
              className={`text-[10px] cursor-pointer whitespace-nowrap transition-colors ${
                angle === tick ? 'font-bold' : ''
              }`}
              style={{ color: angle === tick ? color : '#9ca3af' }}
              onClick={() => onSliderChange(tick)}
            >
              {tick}°
            </span>
          ))}
        </div>
      </div>

      {/* +/- Fine Adjust Buttons */}
      <div className="flex items-center justify-center gap-2 mb-3">
        <button
          onClick={() => onAdjust(-5)}
          disabled={disabled || angle <= ANGLE_MIN}
          className="w-9 h-9 flex items-center justify-center rounded-full text-xs font-semibold cursor-pointer whitespace-nowrap transition-all border border-gray-200 bg-white text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
          title="-5°"
        >
          -5°
        </button>
        <button
          onClick={() => onAdjust(-1)}
          disabled={disabled || angle <= ANGLE_MIN}
          className="w-8 h-8 flex items-center justify-center rounded-full text-xs font-semibold cursor-pointer whitespace-nowrap transition-all border border-gray-200 bg-white text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
          title="-1°"
        >
          -1°
        </button>
        <span className="w-16 text-center font-bold text-lg whitespace-nowrap" style={{ color }}>
          {angle}°
        </span>
        <button
          onClick={() => onAdjust(1)}
          disabled={disabled || angle >= ANGLE_MAX}
          className="w-8 h-8 flex items-center justify-center rounded-full text-xs font-semibold cursor-pointer whitespace-nowrap transition-all border border-gray-200 bg-white text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
          title="+1°"
        >
          +1°
        </button>
        <button
          onClick={() => onAdjust(5)}
          disabled={disabled || angle >= ANGLE_MAX}
          className="w-9 h-9 flex items-center justify-center rounded-full text-xs font-semibold cursor-pointer whitespace-nowrap transition-all border border-gray-200 bg-white text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
          title="+5°"
        >
          +5°
        </button>
      </div>

      {/* Preset Buttons */}
      <div className="flex gap-1.5 flex-wrap">
        {PRESETS.map((preset) => (
          <button
            key={preset.value}
            onClick={() => onPreset(preset.value)}
            disabled={disabled}
            className={`flex-1 min-w-0 px-2 py-1.5 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap transition-all border ${
              angle === preset.value
                ? 'text-white border-transparent'
                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-100'
            } disabled:opacity-30 disabled:cursor-not-allowed`}
            style={
              angle === preset.value
                ? { backgroundColor: color, borderColor: color }
                : undefined
            }
          >
            {preset.label}
          </button>
        ))}
      </div>
    </div>
  );
}